/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import { loadStripe } from '@stripe/stripe-js';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { User } from 'firebase/auth';
import { auth } from './FirestoreService';

// Get Firebase functions instance - it will use the auth from FirestoreService
// We need to import this lazily to ensure FirestoreService initializes first
let functionsInstance: ReturnType<typeof getFunctions> | null = null;

function getFunctionsInstance() {
  if (!functionsInstance) {
    functionsInstance = getFunctions(auth.app, 'us-central1');
    // Uncomment the following line to use the Firebase emulator during local development
    // if (process.env.NODE_ENV === 'development') {
    //   connectFunctionsEmulator(functionsInstance, 'localhost', 5001);
    // }
  }
  return functionsInstance;
}

// Stripe publishable keys:
const TEST_PUBLISHABLE_KEY = 'pk_test_51QaRTSDSTIkj3qOMFb6yhRXsPVNUvpsiSKu6bSHOnAGYQ2towKnLuAXw0UZocwOMNO9ZNZQxfGnQpjrO8YqTmuq900hce56HkH';
const LIVE_PUBLISHABLE_KEY = 'pk_live_51QaRTSDSTIkj3qOMS9pBvGGI6W107ityvY011czuvklFT6y34E7jRxd9u8hi7YMnZ8uhQBdtZQJXoj5WbL10QJDW006BiMGe5x';

// Configuration - switch between test and live mode
// NOTE: Must match IS_TEST_MODE in firebase/functions/stripeHandlers.js
const IS_TEST_MODE = false;

const publishedKey = IS_TEST_MODE ? TEST_PUBLISHABLE_KEY : LIVE_PUBLISHABLE_KEY;
const stripePromise = loadStripe(publishedKey);

export enum SubscriptionTier {
  APPRENTICE = 'Apprentice',
  SMITH = 'Monster Smith',
  MASTERSMITH = 'Master Monster Smith',
  LIFETIME = 'Lifetime'
}

export interface Subscription {
  subscription:    SubscriptionTier,
  status:          string,
  expiration:      Date,
  monstersAllowed: number
}

export interface SubscriptionOption {
  id: SubscriptionTier;
  name: string;
  price: string;
  monstersAllowed: number;
  description: string;
}

export const SUBSCRIPTION_OPTIONS: SubscriptionOption[] = [
  {
    id: SubscriptionTier.SMITH,
    name: "Monster Smith",
    price: "$9.99 / year",
    monstersAllowed: 50,
    description: "Store up to 50 monsters online, plus access to PC/NPC creator."
  },
  {
    id: SubscriptionTier.MASTERSMITH,
    name: "Master Monster Smith",
    price: "$19.99 / year",
    monstersAllowed: 500,
    description: "Store up to 500 monsters online, plus access to PC/NPC creator."
  },
  {
    id: SubscriptionTier.LIFETIME,
    name: "Lifetime Monster Smith",
    price: "$59.99 lifetime access",
    monstersAllowed: 500,
    description: "Store up to 500 monsters online, plus access to PC/NPC creator. No annual fee, never expires!"
  }
];

class StripeService {
  async createCheckoutSession(user: User, selectedTier: SubscriptionTier): Promise<string | null> {
    try {
      if (!user) {
        throw new Error('User must be logged in to subscribe');
      }

      // Make sure the selectedTier is a valid string
      if (!selectedTier || typeof selectedTier !== 'string' || 
          (selectedTier !== SubscriptionTier.SMITH && 
           selectedTier !== SubscriptionTier.MASTERSMITH && 
           selectedTier !== SubscriptionTier.LIFETIME)) {
        throw new Error('Invalid subscription tier selected');
      }

      const idToken = await user.getIdToken();
      console.log('ID Token:', idToken); // Check if the token exists

      const functions = getFunctionsInstance();
      const createStripeCheckout = httpsCallable(functions, 'createStripeCheckout');

      // Get current origin (e.g., http://localhost:3000 or https://greengodgames.com)
      const origin = `${window.location.origin}/monsterforge`;

      // Pass the tier and origin
      const result = await createStripeCheckout({
        tier: selectedTier.toString(),
        origin: origin
      });
      
      // const functionUrl = "https://us-central1-monster-forge.cloudfunctions.net/createStripeCheckout";
      // const response = await fetch(functionUrl, {
      //   method: "POST",
      //   headers: {
      //     "Content-Type": "application/json",
      //     "Authorization": `Bearer ${idToken}`, // 🔑 Attach Firebase Auth token manually
      //   },
      //   body: JSON.stringify({ tier: selectedTier.toString() }),
      // });
      // const result = await response.json();

      console.log("Stripe Checkout Response:", result);

      // The result should contain a sessionId
      if (!result.data || typeof result.data !== 'object') {
        throw new Error('Invalid response from server');
      }
      
      const data = result.data as any;
      if (!data.sessionId) {
        throw new Error('No session ID returned from server');
      }
      
      return data.sessionId;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      return null;
    }
  }
  
  async redirectToCheckout(sessionId: string): Promise<void> {
    try {
      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error('Failed to load Stripe');
      }
      
      const { error } = await stripe.redirectToCheckout({ sessionId });
      
      if (error) {
        console.error('Error redirecting to checkout:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error redirecting to checkout:', error);
      throw error;
    }
  }

  async getCurrentSubscription(user: User): Promise<Subscription | null> {
    try {
      const functions = getFunctionsInstance();
      const getSubscriptionStatus = httpsCallable(functions, 'getSubscriptionStatus');

      const result = await getSubscriptionStatus({});
      const { subscription, status, expiration, monstersAllowed } = (result.data as any);

      console.log('Subscription data received:', { subscription, status, expiration, monstersAllowed });
      console.log('Expiration type:', typeof expiration);
      console.log('Expiration value:', expiration);

      // Convert expiration to Date object
      // Firebase Functions may return it as ISO string, object, or null
      let expirationDate: Date | null = null;
      if (expiration) {
        if (expiration instanceof Date) {
          expirationDate = expiration;
        } else if (typeof expiration === 'string') {
          expirationDate = new Date(expiration);
        } else if (expiration._seconds) {
          // Firestore Timestamp format: {_seconds: number, _nanoseconds: number}
          expirationDate = new Date(expiration._seconds * 1000);
        } else {
          // Try parsing as-is
          expirationDate = new Date(expiration);
        }
      }

      console.log('Parsed expiration date:', expirationDate);

      return {
        subscription: subscription as SubscriptionTier,
        status,
        expiration: expirationDate!,
        monstersAllowed
      };

    } catch (error) {
      console.error('Error getting subscription status:', error);
      return null;
    }
  }
  
  async cancelSubscription(user: User): Promise<boolean> {
    try {
      if (!user) {
        throw new Error('User must be logged in to cancel subscription');
      }

      const functions = getFunctionsInstance();
      const cancelStripeSubscription = httpsCallable(functions, 'cancelSubscription');

      const result = await cancelStripeSubscription({});
      const { success } = (result.data as any);

      return success;
    } catch (error) {
      console.error('Error canceling subscription:', error);
      return false;
    }
  }

  async upgradeSubscription(user: User, newTier: SubscriptionTier): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      if (!user) {
        throw new Error('User must be logged in to upgrade subscription');
      }

      const functions = getFunctionsInstance();
      const upgradeStripeSubscription = httpsCallable(functions, 'upgradeSubscription');

      const result = await upgradeStripeSubscription({
        tier: newTier.toString()
      });
      const { success, message } = (result.data as any);

      return { success, message };
    } catch (error: any) {
      console.error('Error upgrading subscription:', error);
      return {
        success: false,
        error: error.message || 'Failed to upgrade subscription'
      };
    }
  }
}

export const stripeService = new StripeService();